import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { createFilterOptions } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton } from '@mui/material';
import { UserContext } from 'src/store/ContextStore';
import { CONSTANTS } from 'src/components/Constants';

const baseFilter = createFilterOptions();

export default function AutocompleteInput(props) {
  const {
    highlightErrorBorder,
    helperText,
    options = [],
    filterOptions: customFilterOptions,
    onDelete,
    restrictCreate,
    ...rest
  } = props;

  const {
    user: { userType }
  } = React.useContext(UserContext);

  const canCreate = restrictCreate
    ? userType === CONSTANTS.userTypes.ADMIN ||
      userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
    : userType === CONSTANTS.userTypes.ADMIN ||
      userType === CONSTANTS.userTypes.GENETICS ||
      userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN;

  const filterOptions = (opts, params) => {
    const filtered = customFilterOptions
      ? [...customFilterOptions(opts, params)]
      : baseFilter(opts, params);

    if (canCreate && params.inputValue !== '') {
      filtered.push({
        inputValue: params.inputValue,
        label: `Add "${params.inputValue}"`
      });
    }
    return filtered;
  };

  const getLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option.label) return option.label;
    if (option.inputValue) return option.inputValue;
    return option.label;
  };

  const error = props.validated && !props.value;

  const onChange = (event, newValue) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canCreate) {
      // In multiple mode newValue is the full selected array — pass it through
      // directly since freeSolo=false already prevents arbitrary free-text input.
      if (Array.isArray(newValue)) {
        return props.onChange(newValue);
      }
      const label = getLabel(newValue || '');
      const exists = options.some((opt) => getLabel(opt) === label);
      if (!exists) return;
      return props.onChange(newValue);
    }

    if (typeof newValue === 'string') {
      props.onChange({
        label: newValue
      });
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      props.onChange({
        label: newValue.inputValue
      });
    } else {
      props.onChange(newValue);
    }
  };

  const renderOption =
    onDelete && canCreate
      ? (optionProps, option) => (
          <Box
            component="li"
            {...optionProps}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <span style={{ flex: 1 }}>{getLabel(option)}</span>
            {!option.inputValue && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(option);
                }}
                sx={{ ml: 1, p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )
      : undefined;

  return (
    <Autocomplete
      {...rest}
      options={options}
      getOptionLabel={getLabel}
      filterOptions={filterOptions}
      onChange={onChange}
      disableClearable
      forcePopupIcon
      popupIcon={<ArrowDropDownIcon />}
      freeSolo={canCreate}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={undefined}
          className={props.className}
          variant="outlined"
          fullWidth
          error={highlightErrorBorder || error}
          helperText={error && helperText}
        />
      )}
      validated={undefined}
    />
  );
}
